/** Витрина целиком: загрузка каталога, навигация, заказ.
 *
 * Заставка-пар живёт снаружи всех ветвей: она показывается ровно один раз за
 * открытие и не должна перезапускаться от того, что каталог наконец пришёл или
 * не пришёл вовсе. Поэтому здесь два слоя — оболочка с паром и сама витрина.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, request } from '@/shared/api';
import type {
  AccountAnswer, CartInfo, Catalog, GamesView, OrderResult, Product, ProductList, Reward,
} from '@/shared/types';
import { Note, Skeleton, useToast } from '@/shared/ui';
import { openManager, tap, tg } from '@/shared/tg';
import { useNavigation, type Screen, type Tab } from './nav';
import { FailScreen } from '@/shared/FailScreen';
import { pathToList } from './diagnostics';
import { AccountScreen, MyOrdersScreen, ReferralScreen } from './account';
import { MarketScreen } from './market';
import { CartScreen, useCart } from './cart';
import { TabBar } from './TabBar';
import { Disclaimer, GateClosed } from './Disclaimer';
import { Notices } from './Notices';
import { track } from './track';
import { GameScreen, GamesHub, loadGamesView } from './games';
import { RaffleScreen } from './raffle';
import { SmokeLoader } from './Smoke';
import { Topbar } from './Topbar';
import { useStockPulse } from './pulse';
import {
  MenuScreen,
  MessageScreen,
  ProductGrid,
  ProductScreen,
  SearchResults,
} from './screens';

/** Корзина выключена — хуку всё равно нужно что-то держать в руках. */
const NO_CART: CartInfo = { cap: 0, caps: [], express: null, phone: '', addresses: [] };

export function App() {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [openTo, setOpenTo] = useState<Screen[] | null>(null);
  /** ask — спрашиваем возраст, closed — отказался, open — можно смотреть */
  const [gate, setGate] = useState<'ask' | 'closed' | 'open'>('open');
  // Пар начал расходиться — под ним уже можно ставить следующий слой
  const [revealing, setRevealing] = useState(false);

  /** Перечитать витрину: наличие ушло вперёд, и потолки в руках устарели. */
  const reloadCatalog = useCallback(() => {
    request<Catalog>('/api/catalog', { source: 'shop' })
      .then(setCatalog)
      .catch(() => undefined);
  }, []);

  // Склад живёт своей жизнью: пока витрина открыта, кто-то забирает последнее.
  // Догоняем сами, чтобы человек не узнавал об этом отказом на оформлении
  useStockPulse(reloadCatalog);

  useEffect(() => {
    request<Catalog>('/api/catalog', { source: 'shop' })
      .then((data) => {
        // Кнопка из чата ведёт в конкретный раздел — открываем сразу его,
        // выстроив путь от корня, чтобы крошки и «назад» вели куда надо
        const wanted = new URLSearchParams(location.search).get('list');
        if (wanted && data.lists[wanted]) setOpenTo(pathToList(data, wanted));
        if (data.disclaimer?.needed) setGate('ask');
        setCatalog(data);
      })
      .catch((error: ApiError) => setFailure(error));
  }, []);

  // Размытие и вопрос появляются одним движением. Раньше стекло вставало
  // сразу после заставки, а карточка ждала своей анимации — между ними
  // повисала секунда пустого размытого экрана, и было непонятно, сломалось
  // что-то или нет.
  const asking = gate === 'ask' && revealing;
  // За стеклом каталог остаётся и после отказа: экран «закрыто» не должен
  // висеть поверх живой, листаемой витрины
  const gated = asking || gate === 'closed';

  return (
    <>
      {failure ? (
        <FailScreen error={failure} surface="shop" />
      ) : catalog ? (
        // Каталог остаётся на месте и за размытием: видно, что там есть что
        // смотреть, — вопрос на пороге, а не запертая дверь
        <div className={gated ? 'behind-gate' : undefined} inert={gated || undefined}>
          <Storefront catalog={catalog} openTo={openTo} onStale={reloadCatalog} />
        </div>
      ) : (
        <div className="grid gap-2.5 pt-4">
          {[0, 1, 2, 3, 4].map((index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      )}

      {/* Ворота поднимаются только после заставки: отвечать на вопрос, ещё не
          увидев, куда попал, — значит отвечать наугад */}
      {catalog?.disclaimer && asking && (
        <Disclaimer gate={catalog.disclaimer} onAnswer={(agreed) => setGate(agreed ? 'open' : 'closed')} />
      )}
      {catalog?.disclaimer && gate === 'closed' && <GateClosed gate={catalog.disclaimer} />}

      {/* Уведомления ждут своей очереди: сперва человек отвечает про возраст,
          и только потом ему есть смысл что-то рассказывать */}
      <Notices ready={revealing && gate === 'open'} />

      <SmokeLoader
        ready={catalog !== null || failure !== null}
        onClearing={() => setRevealing(true)}
      />
    </>
  );
}

function Storefront({
  catalog,
  openTo,
  onStale,
}: {
  catalog: Catalog;
  openTo: Screen[] | null;
  /** Витрина отстала от склада — перечитать каталог со свежими потолками */
  onStale: () => void;
}) {
  // Режим корзины включает владелец; без него всё работает по-старому.
  // Хук зовём всегда: витрина перечитывается на ходу, и режим может смениться
  // прямо под открытым экраном — порядок хуков от этого меняться не должен
  const cartApi = useCart(catalog.cart ?? NO_CART);
  const cart = catalog.cart ? cartApi : null;
  const [query, setQuery] = useState('');
  const [ordering, setOrdering] = useState(false);
  const [orderState, setOrderState] = useState<'idle' | 'sent'>('idle');
  const { toast, show } = useToast();
  // Кошелёк держим здесь: он нужен панели заказа и обновляется после отправки
  const [rewards, setRewards] = useState<Reward[]>([]);

  const loadRewards = useCallback(() => {
    request<AccountAnswer>('/api/account', {})
      .then((answer) => setRewards(answer.rewards))
      .catch(() => undefined);
  }, []);

  useEffect(loadRewards, [loadRewards]);

  const { tab, current, stack, stacks, tops, push, pop, goTo, openPath, openTab } =
    useNavigation(catalog.root_id);

  // Кошелёк перечитывается на входе в корзину: бонус, купленный в маркете
  // минуту назад, обязан быть в опциях заказа сразу, а не после перезапуска
  // витрины. Покупка тоже дёргает перечитку, но вкладки живут своей жизнью,
  // и вход — единственный момент, который ловит всё
  useEffect(() => {
    if (tab === 'cart') loadRewards();
  }, [tab, loadRewards]);

  // Вкладка игр появляется, только если раздел включён: пустая хуже отсутствующей
  const [games, setGames] = useState<GamesView | null>(null);
  useEffect(() => {
    loadGamesView().then(setGames).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (openTo) openPath(openTo);
  }, [openTo, openPath]);

  // Розыгрыш завершили, пока человек смотрел его вкладку: вкладки больше
  // нет, и оставлять его на экране без полосы под ним нельзя
  useEffect(() => {
    if (tab === 'raffle' && !catalog.raffle) openTab('catalog');
  }, [tab, catalog.raffle, openTab]);

  // Заказ отправлен — экран товара больше не про выбор, а про ожидание
  useEffect(() => {
    setOrderState('idle');
  }, [current]);

  // Куда человек зашёл. Название копией: раздел переименуют, а в истории
  // должно остаться то, что человек видел
  useEffect(() => {
    if (current.type === 'menu') track('menu', current.id, current.title);
    else if (current.type === 'list') track('list', current.id, current.title);
    else if (current.type === 'product') track('product', current.id, current.title);
    else if (current.type === 'account') track('account', 'account', 'Личный кабинет');
    else if (current.type === 'cart') track('cart', 'cart', 'Корзина');
  }, [current]);

  const order = useCallback(
    async (product: Product, list: ProductList, variants: string[],
           rewardId: number | null) => {
      setOrdering(true);
      try {
        const result = await request<OrderResult>('/api/order', {
          list_id: list.id,
          product_id: product.id,
          variants,
          reward_id: rewardId,
        });
        loadRewards();
        tap('success');
        setOrderState('sent');
        show(
          result.sent_to_manager
            ? 'Заявка отправлена менеджеру'
            : 'Заявка сохранена, напишите менеджеру',
        );
        setTimeout(() => tg()?.close(), 900);
      } catch (error) {
        tap('error');
        if ((error as ApiError).message === 'too_fast') {
          // Сервер держит частоту заявок: подождать, а не бежать к менеджеру
          show('Слишком часто. Подождите немного и отправьте снова');
          return;
        }
        show(`Не отправилось: ${(error as ApiError).message}`);
        // Заявка не ушла — человека нельзя оставлять ни с чем: открываем
        // менеджера, чтобы заказ можно было проговорить словами
        openManager(catalog.manager_url);
      } finally {
        setOrdering(false);
      }
    },
    [show, catalog.manager_url, loadRewards],
  );

  const openProduct = (product: Product, list: ProductList) =>
    push({
      type: 'product',
      id: product.id,
      listId: list.id,
      title: product.text.split('\n')[0] ?? '',
    });

  // Поиск живёт только в корне каталога; на других вкладках его нет и подавно
  const searchable = stacks.catalog.length === 1 && tops.catalog.type === 'menu';

  /** Экран вкладки. Соседей рисуем тем же кодом: подсмотр не должен отличаться
      от того, куда человек приедет. */
  const screenBody = (screen: Screen, visible: boolean): React.ReactNode => {
    if (screen.type === 'menu') {
      const menu = catalog.menus[screen.id];
      if (!menu) return <Note tone="warn">Меню не найдено.</Note>;
      return searchable && query.trim() ? (
        <SearchResults catalog={catalog} query={query} onOpen={openProduct} />
      ) : (
        <MenuScreen menu={menu} catalog={catalog} onOpen={push} />
      );
    }
    if (screen.type === 'list') {
      const list = catalog.lists[screen.id];
      return list ? (
        <ProductGrid list={list} onOpen={(product) => openProduct(product, list)} />
      ) : (
        <Note tone="warn">Список не найден.</Note>
      );
    }
    if (screen.type === 'product') {
      const list = catalog.lists[screen.listId];
      const product = list?.products.find((item) => item.id === screen.id);
      return list && product ? (
        <ProductScreen
          product={product}
          list={list}
          ordering={ordering}
          orderState={orderState}
          rewards={cart ? [] : rewards}
          cart={cart}
          onToast={show}
          onOrder={(variants, rewardId) => order(product, list, variants, rewardId)}
        />
      ) : (
        <Note tone="warn">Товар больше не доступен.</Note>
      );
    }
    if (screen.type === 'message') {
      return <MessageScreen text={screen.text} managerUrl={catalog.manager_url} />;
    }
    if (screen.type === 'account') {
      return <AccountScreen onOpen={push} managerUrl={catalog.manager_url} />;
    }
    if (screen.type === 'cart') {
      return cart ? (
        <CartScreen
          cart={cart}
          catalog={catalog}
          rewards={rewards}
          onStale={onStale}
          onCatalog={() => openTab('catalog')}
          onToast={show}
          onDone={(orderId) => {
            loadRewards();
            show(`Заказ №${orderId} отправлен менеджеру`);
            setTimeout(() => tg()?.close(), 1200);
          }}
        />
      ) : (
        <Note tone="warn">Корзина сейчас выключена.</Note>
      );
    }
    if (screen.type === 'my-orders') return <MyOrdersScreen />;
    if (screen.type === 'market') return <MarketScreen onBought={loadRewards} />;
    if (screen.type === 'referral') return <ReferralScreen />;
    if (screen.type === 'games') return <GamesHub onOpen={push} visible={visible} />;
    if (screen.type === 'raffle') return <RaffleScreen onCatalog={() => openTab('catalog')} />;
    if (screen.type === 'game') {
      return <GameScreen gameId={screen.id} onMissing={pop} onToast={show} />;
    }
    return null;
  };

  /** Страница целиком: поиск живёт вместе с корнем каталога, а не над всем. */
  const page = (which: Tab, screen: Screen, visible: boolean) => (
    <>
      {which === 'catalog' && searchable && (
        <div className="mb-3">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск по товарам"
            className="w-full rounded-xl border border-glass-line bg-glass px-4 py-3 text-base text-frost outline-none transition-colors duration-150 placeholder:text-hint focus:border-mint focus:bg-glass-2"
          />
        </div>
      )}
      {screenBody(screen, visible)}
    </>
  );

  const title =
    current.type === 'list'
      ? current.title
      : current.type === 'product'
        ? (catalog.lists[current.listId]?.title ?? current.title)
        : current.title;

  return (
    <>
      <Topbar stack={stack} title={title} onBack={pop} onCrumb={goTo} />

      {page(tab, current, true)}

      <TabBar tab={tab} onOpen={openTab} games={games} rewards={rewards}
              cartCount={cart ? cart.count : -1} raffle={catalog.raffle ?? null} />

      {toast}
    </>
  );
}
